import {MinusCircleOutlined, PlusCircleOutlined} from '@ant-design/icons';
import {Alert, Divider, Form, Input} from 'antd';
import {FormInstance} from 'antd/es/form';
import cn from 'classnames';
import React, {Key, useEffect, useState} from 'react';

import {CustomSelect} from 'components/form/inputs/custom-select';

import {IRisksRequestDetailsDto} from '../../api/types';
import {DocumentsBlock} from './documents-block';
import styles from './request-details-form.module.less';

interface IRequestDetailsFormProps {
    formInstance: FormInstance;
    details: IRisksRequestDetailsDto;
    isEditMode: boolean;
}

export const RequestDetailsForm = ({formInstance, details, isEditMode}: IRequestDetailsFormProps) => {
    const [hiddenDocs, setHiddenDocs] = useState<Record<Key, boolean>>({});

    const toggleDocs = (key: Key) => {
        setHiddenDocs((state) => ({...state, [key]: !state[key]}));
    };

    const hasAdditional = Boolean(
        details.aisDocId
            || details.reportingYear
            || details.taxCode
            || details.dateFrom
            || details.dateTo
            || details.cpartyInn
            || details.cpartyKpp
            || details.cpartyForeignRegNum
    );

    useEffect(() => {
        formInstance.resetFields();
        formInstance.setFieldsValue({
            requestId: details.requestId,
            requestTypeName: details.requestTypeName,
            createdRequestDate: details.createdRequestDate,
            statusName: details.statusName,
            plannedResponseDate: details.plannedResponseDate,
            factResponseDate: details.factResponseDate,
            responseId: details.responseId,
            aisDocId: details.aisDocId,
            reportingYear: details.reportingYear,
            taxCode: details.taxCode,
            dateFrom: details.dateFrom,
            dateTo: details.dateTo,
            cpartyInn: details.cpartyInn,
            cpartyKpp: details.cpartyKpp,
            cpartyForeignRegNum: details.cpartyForeignRegNum,
            risks: details.risks,
        });
        setHiddenDocs({});
    }, [details]);

    return (
        <Form
            layout="vertical"
            form={formInstance}
        >
            <section>
                <h4 className={styles['request-details-form__title']}>Основные сведения</h4>
                <div className="d-flex gap-2">
                    <Form.Item
                        name="requestId"
                        label="Номер запроса"
                        className={styles['request-details-form__flex-item']}
                    >
                        <Input disabled />
                    </Form.Item>
                    <Form.Item
                        name="requestTypeName"
                        label="Тип запроса"
                        className={styles['request-details-form__flex-item']}
                    >
                        <Input disabled />
                    </Form.Item>
                </div>
                <div className="d-flex gap-2">
                    <Form.Item
                        name="createdRequestDate"
                        label="Дата запроса"
                        className={styles['request-details-form__flex-item']}
                    >
                        <Input disabled />
                    </Form.Item>
                    <Form.Item
                        name="statusName"
                        label="Статус"
                        className={styles['request-details-form__flex-item']}
                    >
                        <Input disabled />
                    </Form.Item>
                </div>
                <div className="d-flex gap-2">
                    <Form.Item
                        name="plannedResponseDate"
                        label="Плановая дата ответа"
                        className={styles['request-details-form__flex-item']}
                    >
                        <Input disabled />
                    </Form.Item>
                    <Form.Item
                        name="factResponseDate"
                        label="Фактическая дата ответа"
                        className={styles['request-details-form__flex-item']}
                    >
                        <Input disabled />
                    </Form.Item>
                    <Form.Item
                        name="responseId"
                        label="Номер ответа"
                        className={styles['request-details-form__flex-item']}
                    >
                        <Input disabled />
                    </Form.Item>
                </div>
            </section>

            <Divider />

            {hasAdditional && (
                <section>
                    <h4 className={styles['request-details-form__title']}>Дополнительные данные по запросу</h4>
                    <div className={styles['request-details-form__additional-fields']}>
                        {details.aisDocId && (
                            <Form.Item
                                name="aisDocId"
                                label="Идентификатор документа в ИС НП"
                            >
                                <Input disabled />
                            </Form.Item>
                        )}
                        {details.reportingYear && (
                            <Form.Item
                                name="reportingYear"
                                label="Отчётный год"
                            >
                                <Input disabled />
                            </Form.Item>
                        )}
                        {details.taxCode && (
                            <Form.Item
                                name="taxCode"
                                label="Код налога"
                            >
                                <Input disabled />
                            </Form.Item>
                        )}
                        {details.dateFrom && (
                            <Form.Item
                                name="dateFrom"
                                label="Дата с"
                            >
                                <Input disabled />
                            </Form.Item>
                        )}
                        {details.dateTo && (
                            <Form.Item
                                name="dateTo"
                                label="Дата по"
                            >
                                <Input disabled />
                            </Form.Item>
                        )}
                        {details.cpartyInn && (
                            <Form.Item
                                name="cpartyInn"
                                label="ИНН контрагента"
                            >
                                <Input disabled />
                            </Form.Item>
                        )}
                        {details.cpartyKpp && (
                            <Form.Item
                                name="cpartyKpp"
                                label="КПП контрагента"
                            >
                                <Input disabled />
                            </Form.Item>
                        )}
                        {details.cpartyForeignRegNum && (
                            <Form.Item
                                name="cpartyForeignRegNum"
                                label="Регистрационный номер контрагента в стране регистрации"
                            >
                                <Input disabled />
                            </Form.Item>
                        )}
                    </div>
                </section>
            )}

            <Divider />

            {details.risks && details.risks.length > 0 && (
                <section>
                    <h4 className={styles['request-details-form__title']}>Информация о рисках</h4>
                    <Form.List name="risks">
                        {(riskFields) => (
                            <>
                                {riskFields.map((riskField) => (
                                    <Form.Item
                                        key={riskField.key}
                                        noStyle
                                        shouldUpdate={(prev, next) =>
                                            prev?.risks?.[riskField.name]?.documents
                                            !== next?.risks?.[riskField.name]?.documents
                                        }
                                    >
                                        {(form) => {
                                            const risk = details.risks[riskField.name] || {};
                                            const documents =
                                                form.getFieldValue(['risks', riskField.name, 'documents']) || [];
                                            const hasDocuments = documents.length > 0;
                                            const isDocsHidden = !!hiddenDocs[riskField.key];
                                            const ToggleIcon = isDocsHidden
                                                ? PlusCircleOutlined
                                                : MinusCircleOutlined;
                                            const hasAnyActive = documents.some(
                                                (d: {activeFlag?: boolean}) => d?.activeFlag
                                            );
                                            const showAlert =
                                                !isDocsHidden && isEditMode && hasDocuments && !hasAnyActive;

                                            return (
                                                <div className={styles['request-details-form__risk']}>
                                                    {hasDocuments && (
                                                        <ToggleIcon
                                                            className={styles['request-details-form__risk-icon']}
                                                            onClick={() => toggleDocs(riskField.key)}
                                                        />
                                                    )}
                                                    <div
                                                        className={cn(styles['request-details-form__risk-content'], {
                                                            [styles['request-details-form__risk-content--collapsed']]:
                                                                isDocsHidden || !hasDocuments,
                                                        })}
                                                    >
                                                        <div className="d-flex gap-2">
                                                            <Form.Item
                                                                name={[riskField.name, 'riskCode']}
                                                                label="Код риска"
                                                                className={styles['request-details-form__flex-item']}
                                                            >
                                                                <Input disabled />
                                                            </Form.Item>
                                                            {risk.taxPeriod && (
                                                                <Form.Item
                                                                    name={[riskField.name, 'taxPeriod']}
                                                                    label="Код налогового (отчётного) периода"
                                                                    className={styles['request-details-form__flex-item']}
                                                                >
                                                                    <Input disabled />
                                                                </Form.Item>
                                                            )}
                                                            {risk.corrNum && (
                                                                <Form.Item
                                                                    name={[riskField.name, 'corrNum']}
                                                                    label="Номер корректировки"
                                                                    className={styles['request-details-form__flex-item']}
                                                                >
                                                                    <Input disabled />
                                                                </Form.Item>
                                                            )}
                                                        </div>
                                                        <div className="d-flex gap-2">
                                                            <Form.Item
                                                                name={[riskField.name, 'hasRisk']}
                                                                label="Признак наличия риска"
                                                                className={styles['request-details-form__flex-item']}
                                                            >
                                                                <CustomSelect
                                                                    settings={{
                                                                        isDisabled: true,
                                                                        url: 'valueLists/FLEX_VALUE?sublistCode=AIS_SVC_HAS_RISK',
                                                                        valuePath: 'id',
                                                                        labelPath: 'description',
                                                                    }}
                                                                />
                                                            </Form.Item>
                                                            {risk.riskLevel && (
                                                                <Form.Item
                                                                    name={[riskField.name, 'riskLevel']}
                                                                    label="Уровень риска"
                                                                    className={styles['request-details-form__flex-item']}
                                                                >
                                                                    <CustomSelect
                                                                        settings={{
                                                                            isDisabled: true,
                                                                            url: 'valueLists/FLEX_VALUE?sublistCode=AIS_RISK_GRADE',
                                                                            valuePath: 'id',
                                                                            labelPath: 'description',
                                                                        }}
                                                                    />
                                                                </Form.Item>
                                                            )}
                                                        </div>
                                                        {risk.riskComment && (
                                                            <Form.Item
                                                                name={[riskField.name, 'riskComment']}
                                                                label="Комментарий к риску"
                                                            >
                                                                <Input.TextArea
                                                                    disabled
                                                                    rows={2}
                                                                />
                                                            </Form.Item>
                                                        )}

                                                        {hasDocuments && !isDocsHidden && (
                                                            <DocumentsBlock
                                                                riskField={riskField}
                                                                formInstance={formInstance}
                                                                isEditMode={isEditMode}
                                                            />
                                                        )}
                                                    </div>

                                                    {showAlert && (
                                                        <Alert
                                                            className={styles['request-details-form__alert']}
                                                            type="warning"
                                                            showIcon
                                                            message={
                                                                'Для данного риска отсутствуют активные документы. '
                                                                + 'При отправке запроса для соответствующего кода риска '
                                                                + 'будет указана информация об отсутствии риска.'
                                                            }
                                                        />
                                                    )}
                                                </div>
                                            );
                                        }}
                                    </Form.Item>
                                ))}
                            </>
                        )}
                    </Form.List>
                </section>
            )}
        </Form>
    );
};
